import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useProposal } from "../contexts/ProposalContext";

// Components
import Layout from "../components/layout/Layout";
import DocumentUpload from "../components/documents/DocumentUpload";
import DocumentList from "../components/documents/DocumentList";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";

const DocumentManagement = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const {
    currentProposal,
    fetchProposal,
    loading: proposalLoading,
  } = useProposal();

  const [selectedProposalId, setSelectedProposalId] = useState(
    proposalId ? parseInt(proposalId) : null
  );
  const [uploadSuccess, setUploadSuccess] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [activeTab, setActiveTab] = useState("upload");

  useEffect(() => {
    if (proposalId) {
      fetchProposal(parseInt(proposalId));
      setSelectedProposalId(parseInt(proposalId));
    }
  }, [proposalId, fetchProposal]);

  const handleUploadComplete = (results) => {
    const successful = results.filter((r) => r.success);
    const failed = results.filter((r) => !r.success);

    if (successful.length > 0) {
      setUploadSuccess(
        `Successfully uploaded ${successful.length} document(s)`
      );
      setRefreshTrigger((prev) => prev + 1); // Trigger document list refresh

      // Clear success message after 5 seconds
      setTimeout(() => setUploadSuccess(""), 5000);
    }

    if (failed.length > 0) {
      setUploadError(
        `Failed to upload ${failed.length} document(s): ${failed
          .map((f) => f.error)
          .join(", ")}`
      );
    }
  };

  const handleUploadError = (error) => {
    setUploadError(error);
  };

  const handleDocumentDeleted = () => {
    setRefreshTrigger((prev) => prev + 1); // Trigger document list refresh
  };

  const clearMessages = () => {
    setUploadSuccess("");
    setUploadError("");
  };

  if (proposalLoading && proposalId) {
    return (
      <Layout title="Document Management">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" text="Loading proposal..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={
        currentProposal
          ? `Documents - ${currentProposal.proposal_name}`
          : "Document Management"
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                <FontAwesomeIcon
                  icon="file-alt"
                  className="mr-3 text-primary-500"
                />
                Document Management
              </h1>
              {currentProposal && (
                <p className="text-slate-600 mt-2">
                  Managing documents for{" "}
                  <span className="font-medium">
                    {currentProposal.proposal_name}
                  </span>
                  {currentProposal.customer_name && (
                    <span className="text-slate-500">
                      {" "}
                      • {currentProposal.customer_name}
                    </span>
                  )}
                </p>
              )}
            </div>

            {currentProposal && (
              <button
                onClick={() =>
                  navigate(`/proposal-details/${currentProposal.proposal_id}`)
                }
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="arrow-left" />
                <span>Back to Proposal</span>
              </button>
            )}
          </div>
        </div>

        {/* Status Messages */}
        {uploadSuccess && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon="check" className="text-emerald-500" />
              <span className="text-emerald-700">{uploadSuccess}</span>
            </div>
            <button
              onClick={clearMessages}
              className="text-emerald-600 hover:text-emerald-800"
            >
              <FontAwesomeIcon icon="times" />
            </button>
          </div>
        )}

        {uploadError && (
          <ErrorMessage message={uploadError} onRetry={clearMessages} />
        )}

        {/* No Proposal Selected State */}
        {!selectedProposalId && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
            <FontAwesomeIcon
              icon="file-alt"
              className="text-4xl text-slate-300 mb-4"
            />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              Select a Proposal
            </h3>
            <p className="text-slate-600 mb-6">
              Choose a proposal to manage its documents, or create a new
              proposal first.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate("/proposals")}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-200"
              >
                View Proposals
              </button>
              <button
                onClick={() => navigate("/proposals/new")}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors duration-200"
              >
                Create New Proposal
              </button>
            </div>
          </div>
        )}

        {/* Main Content */}
        {selectedProposalId && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            {/* Tabs */}
            <div className="border-b border-slate-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab("upload")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === "upload"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <FontAwesomeIcon icon="upload" className="mr-2" />
                  Upload Documents
                </button>
                <button
                  onClick={() => setActiveTab("manage")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === "manage"
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <FontAwesomeIcon icon="file-alt" className="mr-2" />
                  Manage Documents
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === "upload" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-2">
                      Upload New Documents
                    </h2>
                    <p className="text-slate-600">
                      Upload RFP documents, past proposals, resumes, and other
                      supporting materials. Documents will be processed and made
                      available for AI analysis.
                    </p>
                  </div>

                  <DocumentUpload
                    proposalId={selectedProposalId}
                    onUploadComplete={handleUploadComplete}
                    onUploadError={handleUploadError}
                  />

                  {/* Upload Tips */}
                  <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                      <FontAwesomeIcon icon="lightbulb" className="mr-2" />
                      Upload Tips
                    </h3>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>
                        • Documents are automatically processed and analyzed by
                        AI
                      </li>
                      <li>
                        • Text extraction works best with clean, high-quality
                        documents
                      </li>
                      <li>
                        • Document types help organize and categorize your files
                      </li>
                      <li>
                        • Processing time depends on document size and
                        complexity
                      </li>
                      <li>
                        • Uploaded documents become searchable in AI chat
                        sessions
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === "manage" && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-2">
                      Document Library
                    </h2>
                    <p className="text-slate-600">
                      View, organize, and manage all documents for this
                      proposal.
                    </p>
                  </div>

                  <DocumentList
                    proposalId={selectedProposalId}
                    onDocumentDeleted={handleDocumentDeleted}
                    refreshTrigger={refreshTrigger}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {/* {selectedProposalId && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() =>
                  navigate(`/chat?proposalId=${selectedProposalId}`)
                }
                className="flex items-center justify-center space-x-2 p-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg transition-colors duration-200"
              >
                <FontAwesomeIcon icon="robot" className="text-emerald-600" />
                <span className="font-medium text-emerald-700">
                  Chat with AI
                </span>
              </button>

              <button
                onClick={() => {
                  // Future: Generate document summary
                  alert("Document summary feature coming soon!");
                }}
                className="flex items-center justify-center space-x-2 p-4 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors duration-200"
              >
                <FontAwesomeIcon icon="chart-bar" className="text-blue-600" />
                <span className="font-medium text-blue-700">
                  Generate Summary
                </span>
              </button>

              <button
                onClick={() => {
                  // Future: Export documents
                  alert("Export feature coming soon!");
                }}
                className="flex items-center justify-center space-x-2 p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors duration-200"
              >
                <FontAwesomeIcon icon="download" className="text-slate-600" />
                <span className="font-medium text-slate-700">Export All</span>
              </button>
            </div>
          </div>
        )} */}
      </div>
    </Layout>
  );
};

export default DocumentManagement;
